import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import styled from 'styled-components';
import Anchor from '../../components/Anchor/Anchor';
import BrandRow from '../../components/BrandTable/BrandRow';
import BulletItem from '../../components/BulletItem/BulletItem';
import BulletList from '../../components/BulletList/BulletList';
import Button from '../../components/Button/Button';
import AgreementCheckbox from '../../components/InputFields/AgreementCheckbox';
import Checkbox from '../../components/InputFields/Checkbox';
import NumberField from '../../components/InputFields/NumberField';
import PasswordField from '../../components/InputFields/PasswordField';
import SelectField from '../../components/InputFields/SelectField';
import TextField from '../../components/InputFields/TextField';
import Paragraph from '../../components/Paragraph/Paragraph';
import {
  transactionProductTableBody,
  transactionProductTableHead,
  transactionProductTableRow,
} from '../../components/Table/Components/TransactionProductTable';
import Table from '../../components/Table/Table';
import TableBody from '../../components/Table/TableBody';
import TableHead from '../../components/Table/TableHead';
import TableRow from '../../components/Table/TableRow';
import Radio from '../../components/InputFields/Radio';
import TypeCard from '../../components/TypeCard/TypeCard';
import Logo from '../../components/Logo/Logo';
import { PortfolioTypeButton } from '../../components/Portfolio/Header/PortfolioType';
import { PortfolioTypesWrap } from '../../components/Portfolio/Header/PortfolioHeader';
import themeColors from '../../assets/styles/themeColors';
import { pxToRem } from '../../assets/styles/helper';

import { ReactComponent as ArrowRight } from '../../assets/images/svg/chevron-right.svg';
import { ReactComponent as AlertTriangle } from '../../assets/images/svg/alert-triangle.svg';
import { ReactComponent as WheelchairIcon } from '../../assets/images/svg/wheelchair.svg';
import { ReactComponent as DeleteIcon } from '../../assets/images/svg/delete-icon.svg';
import { ReactComponent as DropdownIcon } from '../../assets/images/svg/dropdown.svg';
import { ReactComponent as Hamburger } from '../../assets/images/svg/hamburger.svg';
import { ReactComponent as User } from '../../assets/images/svg/user-icon.svg';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import { ReactComponent as Search } from '../../assets/images/svg/search.svg';
import { ReactComponent as EyeOn } from '../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/images/svg/eye-off.svg';
import { ReactComponent as CapsLock } from '../../assets/images/svg/caps-lock.svg';
import { ReactComponent as Checked } from '../../assets/images/svg/checked.svg';
import { ReactComponent as Wallet } from '../../assets/images/svg/wallet-round.svg';
import { ReactComponent as Location } from '../../assets/images/svg/location-round.svg';
import { ReactComponent as Clock } from '../../assets/images/svg/clock-round.svg';
import { ReactComponent as Check } from '../../assets/images/svg/check-round.svg';
import { ReactComponent as CardRound } from '../../assets/images/svg/card-round.svg';
import { ReactComponent as UnsuccessfulIcon } from '../../assets/images/svg/unsuccessful.svg';
import { ReactComponent as Dollar } from '../../assets/images/svg/dollar-round.svg';
import { ReactComponent as QuestionMark } from '../../assets/images/svg/question.svg';
import { ReactComponent as CopyIcon } from '../../assets/images/svg/copy.svg';
import { ReactComponent as WrenchIcon } from '../../assets/images/svg/wrench.svg';
import { ReactComponent as Upload } from '../../assets/images/svg/upload.svg';
import { ReactComponent as OutgoingIcon } from '../../assets/images/svg/outgoing.svg';
import { ReactComponent as ExecutedIcon } from '../../assets/images/svg/executed.svg';
import { ReactComponent as PendingIcon } from '../../assets/images/svg/pending.svg';
import { ReactComponent as SettledIcon } from '../../assets/images/svg/settled.svg';
import { ReactComponent as ShippedIcon } from '../../assets/images/svg/shipped.svg';
import { ReactComponent as PaidIcon } from '../../assets/images/svg/paid.svg';
import { ReactComponent as NotExecuted } from '../../assets/images/svg/notExecuted.svg';
import { ReactComponent as Hold } from '../../assets/images/svg/hold.svg';
import { ReactComponent as FilterIcon } from '../../assets/images/svg/filter.svg';
import { ReactComponent as MaintenanceIcon } from '../../assets/images/svg/maintenance.svg';
import { ReactComponent as VisaIcon } from '../../assets/images/svg/visa.svg';
import { ReactComponent as JcbIcon } from '../../assets/images/svg/jcb.svg';
import { ReactComponent as MasterCardIcon } from '../../assets/images/svg/mastercard.svg';
import { ReactComponent as AmericanExpressIcon } from '../../assets/images/svg/american-express.svg';
import { ReactComponent as DiscoverIcon } from '../../assets/images/svg/discover.svg';
import { ReactComponent as DinersClubIcon } from '../../assets/images/svg/diners.svg';
import { ReactComponent as FrontOfId } from '../../assets/images/svg/front-of-card.svg';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';

const StyleguideContainer = styled.div`
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${pxToRem(30)};
  padding: ${pxToRem(24)};
`;

const StyleguideComponent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${pxToRem(24)};
`;

const ComponentTitle = styled.h2`
  font-size: ${pxToRem(20)} !important;
  font-weight: 600;
  border-left: 3px solid ${themeColors.colorPrimary};
  margin-left: -15px;
  padding-left: 11px;
  margin-bottom: 20px;
`;

const ParagraphWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${pxToRem(10)};

  h1 {
    font-size: ${pxToRem(36)};
    font-weight: 600;
  }
  h2 {
    font-size: ${pxToRem(24)};
  }
  h3 {
    font-size: ${pxToRem(20)};
  }
  h4 {
    font-size: ${pxToRem(18)};
    font-weight: 600;
  }
`;

const LogoWrapper = styled.div`
  svg,
  img {
    color: ${themeColors.colorHeaderLogoDark};
    width: ${pxToRem(200)};
    object-fit: contain;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  svg {
    width: 26px;
    height: 26px;
    margin-right: 20px;
    margin-bottom: 16px;
  }
`;

const IconWrapper = styled.div`
  svg {
    width: auto;
  }
`;

const ComponentWrapper = styled.div`
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  padding-bottom: 16px;
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${pxToRem(20)};
`;

const RadioWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${pxToRem(14)};
  margin-top: 30px;
`;

const TwoColumnsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${pxToRem(70)};
`;

const StyleguideHeader = styled.div`
  background-color: ${themeColors.colorTextPrimary};
  padding: 24px 50px;

  h1,
  h2 {
    color: ${themeColors.colorWhite};
  }
`;

const DragContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px dashed ${themeColors.colorTextSecondary};
  background: ${themeColors.colorGrey};
  text-align: center;
  padding: 0.75rem;
  cursor: pointer;
  max-width: 272px;

  svg {
    max-height: 93px;
    margin-bottom: 1.5rem;
  }
`;

const Image = styled.img`
  object-fit: contain;
  width: 100%;
  height: 100%;
  max-height: 17.5rem;
`;

const DragDropArea = styled.div`
  position: absolute;
  z-index: 1;
  background: transparent;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  input {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const headerData = [
  { id: 1, content: 'Header One' },
  { id: 2, content: 'Header Two' },
  { id: 3, content: 'Header Three' },
];

const data = [
  {
    id: 1,
    BrandCode: 'Example One',
    SerialNumber: 'Example One',
    FineWeight: 'Example One',
  },
  {
    id: 2,
    BrandCode: 'Example Two',
    SerialNumber: 'Example Two',
    FineWeight: 'Example Two',
  },
  {
    id: 3,
    BrandCode: 'Example Three',
    SerialNumber: 'Example Three',
    FineWeight: 'Example Three',
  },
  {
    id: 4,
    BrandCode: 'Example Four',
    SerialNumber: 'Example Four',
    FineWeight: 'Example Four',
  },
];

const brandRowData = data.map((brand) => ({
  id: brand.id,
  ...brand,
  content: (
    <BrandRow
      code={brand.BrandCode}
      serial={brand.SerialNumber}
      weight={brand.FineWeight}
    />
  ),
}));

const Styleguide = () => {
  const brokerCode = useBrokerCode();

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isCheckboxAgreementChecked, setIsCheckboxAgreementChecked] = useState(
    false,
  );
  const handleCheckboxCheck = () => {
    setIsCheckboxChecked(!isCheckboxChecked);
  };
  const handleCheckboxAgreementCheck = () => {
    setIsCheckboxAgreementChecked(!isCheckboxAgreementChecked);
  };

  const [favorite, setFavorite] = useState('Example One');

  const handleValueOneChange = () => {
    setFavorite('Example One');
  };
  const handleValueTwoChange = () => {
    setFavorite('Example Two');
  };
  const handleValueThreeChange = () => {
    setFavorite('Example Three');
  };

  return (
    <>
      <StyleguideHeader>
        <h1>Styleguide</h1>
        <h2>GBI - {brokerCode}</h2>
      </StyleguideHeader>
      <StyleguideContainer>
        <ComponentWrapper>
          <ComponentTitle>Brand logo</ComponentTitle>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>
        </ComponentWrapper>

        <ComponentWrapper>
          <ParagraphWrapper>
            <ComponentTitle>Typography</ComponentTitle>
            <h1>Heading Level 1</h1>
            <h2>Heading Level 2</h2>
            <h3>Heading Level 3</h3>
            <h4>Heading Level 4</h4>

            <h3>Sample Copy</h3>
            <Paragraph fontSize={18}>
              Large paragraph: Lorem ipsum dolor sit amet, consectetur
              adipisicing elit. Aliquam deleniti iure optio porro veniam? At
              corporis dicta eligendi fugit nemo nihil nulla porro, praesentium
              quaerat quam qui quis vitae voluptates.
            </Paragraph>
            <Paragraph fontSize={16}>
              Regular paragraph: Lorem ipsum dolor sit amet, consectetur
              adipisicing elit. Aliquam deleniti iure optio porro veniam? At
              corporis dicta eligendi fugit nemo nihil nulla porro, praesentium
              quaerat quam qui quis vitae voluptates.
            </Paragraph>
            <Paragraph fontSize={14}>
              Small paragraph: Lorem ipsum dolor sit amet, consectetur
              adipisicing elit. Aliquam deleniti iure optio porro veniam? At
              corporis dicta eligendi fugit nemo nihil nulla porro, praesentium
              quaerat quam qui quis vitae voluptates.
            </Paragraph>
          </ParagraphWrapper>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Buttons</ComponentTitle>
          <StyleguideComponent>
            <Button size="lg" variant="primary">
              Primary Button
            </Button>
            <Button size="lg" variant="primaryOutlined">
              Primary Outlined Button
            </Button>
            <Button size="lg" variant="default">
              Default Button
            </Button>
          </StyleguideComponent>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Link</ComponentTitle>
          <Anchor bold text="Primary Link Example" />
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Checkbox and radio button</ComponentTitle>
          <TwoColumnsWrapper>
            <div>
              <Checkbox
                checked={isCheckboxChecked}
                onChange={handleCheckboxCheck}
                text="I acknowledge that all sales are final."
                marginBottom={16}
                marginTop={16}
              >
                <Paragraph>Checkbox Example</Paragraph>
              </Checkbox>

              <AgreementCheckbox
                checked={isCheckboxAgreementChecked}
                onChange={handleCheckboxAgreementCheck}
                text="Checkbox agreement example"
                marginBottom={16}
                marginTop={16}
              />
            </div>

            <RadioWrapper>
              <Radio
                value={favorite === 'Example One'}
                onChange={handleValueOneChange}
                selected={favorite === 'Example One'}
                checked={favorite === 'Example One'}
              >
                Radio button One
              </Radio>

              <Radio
                value={favorite === 'Example Two'}
                onChange={handleValueTwoChange}
                selected={favorite === 'Example Two'}
                checked={favorite === 'Example Two'}
              >
                Radio button Two
              </Radio>

              <Radio
                value={favorite === 'Example Three'}
                onChange={handleValueThreeChange}
                selected={favorite === 'Example Three'}
                checked={favorite === 'Example Three'}
              >
                Radio button Three
              </Radio>
            </RadioWrapper>
          </TwoColumnsWrapper>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Tab button</ComponentTitle>
          <PortfolioTypesWrap>
            <PortfolioTypeButton>Tab Button</PortfolioTypeButton>
            <PortfolioTypeButton isSelected>
              Selected Tab Button
            </PortfolioTypeButton>
          </PortfolioTypesWrap>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Forms</ComponentTitle>
          <Formik
            initialValues={{
              textField: '',
              numberField: '',
              currencyField: '',
              passwordField: '',
              selectField: '',
            }}
          >
            <Form>
              <FormContainer>
                <Field
                  label="Text Field"
                  name="textField"
                  component={TextField}
                />
                <Field
                  label="Number Field"
                  name="numberField"
                  component={NumberField}
                />
                <Field
                  label="Password Field"
                  name="passwordField"
                  component={PasswordField}
                />
                <SelectField
                  name="selectField"
                  label="Select Field"
                  options={[
                    { value: 'Example 1', label: 'Example 1' },
                    { value: 'Example 2', label: 'Example 2' },
                  ]}
                />
                <Field
                  disabled
                  label="Disabled Field"
                  name="disabledField"
                  component={TextField}
                />
              </FormContainer>
            </Form>
          </Formik>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Unordered and ordered lists</ComponentTitle>
          <TwoColumnsWrapper>
            <div>
              <BulletList>
                <BulletItem text="Example 1" />
                <BulletItem text="Example 2" />
                <BulletItem text="Example 3" />
                <BulletItem text="Example 4" />
              </BulletList>
            </div>

            <div>
              <BulletList>
                <BulletItem num="1." text="Example 1" />
                <BulletItem num="2." text="Example 2" />
                <BulletItem num="3." text="Example 3" />
                <BulletItem num="4." text="Example 4" />
              </BulletList>
            </div>
          </TwoColumnsWrapper>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Card component</ComponentTitle>
          <TypeCard
            title="Card Title"
            highlight="Card Highlight"
            description="Card Description"
            note="Card Note"
          />
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Table</ComponentTitle>
          <Table>
            <TableHead data={headerData} css={transactionProductTableHead} />
            <TableBody css={transactionProductTableBody}>
              <TableRow
                rowData={brandRowData}
                css={transactionProductTableRow}
              />
            </TableBody>
          </Table>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>File upload</ComponentTitle>
          <DragContainer>
            <Image alt="" />
            <FrontOfId />

            <>
              <Paragraph fontSize={18} marginBottom={12}>
                Drop your image here or upload from your files
              </Paragraph>
              <Paragraph fontSize={14} color={themeColors.colorTextSecondary}>
                Supports: JPG, PNG, or PDF
              </Paragraph>
            </>

            <DragDropArea>
              <input type="file" />
            </DragDropArea>
          </DragContainer>
        </ComponentWrapper>

        <ComponentWrapper>
          <ComponentTitle>Iconography</ComponentTitle>
          <IconsWrapper>
            <AlertTriangle auto />
            <ArrowRight />
            <WheelchairIcon />
            <DeleteIcon />
            <DropdownIcon />
            <Hamburger />
            <User />
            <Close />
            <Search />
            <EyeOn />
            <EyeOff />
            <CapsLock />
            <Checked />
            <Wallet />
            <Location />
            <Clock />
            <Check />
            <CardRound />
            <UnsuccessfulIcon />
            <Dollar />
            <QuestionMark />
            <CopyIcon />
            <WrenchIcon />
            <Upload />
            <IconWrapper>
              <OutgoingIcon />
            </IconWrapper>
            <ExecutedIcon />
            <PendingIcon />
            <SettledIcon />
            <ShippedIcon />
            <PaidIcon />
            <NotExecuted />
            <Hold />
            <FilterIcon />
            <MaintenanceIcon />
            <IconWrapper>
              <VisaIcon />
            </IconWrapper>
            <IconWrapper>
              <JcbIcon />
            </IconWrapper>
            <IconWrapper>
              <MasterCardIcon />
            </IconWrapper>
            <IconWrapper>
              <DinersClubIcon />
            </IconWrapper>
            <IconWrapper>
              <DiscoverIcon />
            </IconWrapper>
            <IconWrapper>
              <AmericanExpressIcon />
            </IconWrapper>
          </IconsWrapper>
        </ComponentWrapper>
      </StyleguideContainer>
    </>
  );
};

export default Styleguide;
